import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';
import {
  Card,
  Container,
  Label,
  TabContent,
  TabPane,
} from 'reactstrap';

import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import DataTableListGift from 'components/App/DataTableListGift';
import Loading from 'components/App/Loading';
import Meta from 'components/App/Meta';
import Pagination from 'components/Pagination';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import { GIFT_LIST_TAB, themeColor } from 'constants/index';
import ENDPOINT from 'constants/endPoint';
import INITIAL_VALUE from 'constants/initialValue';
import { getParamsFormObject } from 'utils';
import cn from 'utils/classnames';
import AddWhiteIcon from 'assets/images/add-white.svg';
import images from 'assets/images';
import buildGiftListPayload from './buildGiftListPayload';

import styles from './ManagerGiftList.module.scss';

const PAGE_TITLE = 'Danh sách quà tặng';
const DEFAULT_LIMIT = 10;

const giftCountFormatter = new Intl.NumberFormat('vi-VN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const GIFT_TYPE_FILTER = {
  EXCLUSIVE: 'EXCLUSIVE',
  VOUCHER: 'VOUCHER',
  LIMITED: 'LIMITED',
};

const GIFT_TYPE_FILTER_OPTIONS = [
  { value: GIFT_TYPE_FILTER.EXCLUSIVE, label: 'Quà Độc Quyền' },
  { value: GIFT_TYPE_FILTER.VOUCHER, label: 'Quà Voucher' },
  { value: GIFT_TYPE_FILTER.LIMITED, label: 'Quà giới hạn' },
];

const NAV_TAB = [
  { title: 'Tất cả', value: GIFT_LIST_TAB.ALL },
  { title: 'Công khai', value: GIFT_LIST_TAB.PUBLIC },
  { title: 'Ẩn', value: GIFT_LIST_TAB.HIDDEN },
  { title: 'Hết hạn', value: GIFT_LIST_TAB.EXPIRED },
];

const breadcrumbList = [
  {
    name: 'Quà tặng',
    isActive: false,
    link: ENDPOINT.MANAGER_GIFT,
  },
  {
    name: PAGE_TITLE,
    isActive: true,
    link: '',
  },
];

const normalizeGiftFromApi = (gift) => ({
  ...gift,
  id: gift.id ?? gift._id,
  giftCode: gift.giftCode || gift.slug,
  exchangedQuantity: gift.exchangedQuantity ?? gift.usedQuantity ?? 0,
  expiryAt: gift.voucherCampaign?.voucherExpiryAt,
  limitStartAt: gift.limitStartAt ?? gift.startAt,
  limitEndAt: gift.limitEndAt ?? gift.endAt,
});

function ManagerGiftList({
  gifts,
  isLoadingGetGifts,
  actionGetGifts,
  actionDeleteGift,
}) {
  const {
    value: isOpenModalDeleteGift,
    setTrue: onOpenModalDeleteGift,
    setFalse: onCloseModalDeleteGift,
    toggle: onToggleModalDeleteGift,
  } = useBoolean(false);

  const [giftIdDelete, setGiftIdDelete] = useState('');
  const history = useHistory();

  const searchQueries = useMemo(() => {
    const params = new URLSearchParams(history.location.search);
    return {
      page: Number(params.get('page')) || 1,
      q: params.get('q') || '',
      giftType: params.get('giftType') || '',
      statusType: params.get('statusType') || '',
    };
  }, [history.location.search]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: searchQueries.q,
      giftType: searchQueries.giftType,
    },
    onSubmit: (values) => {
      const urlParams = { page: '1' };
      const q = values.q.trim();
      if (q) urlParams.q = q;
      if (values.giftType) urlParams.giftType = values.giftType;
      if (searchQueries.statusType) urlParams.statusType = searchQueries.statusType;
      history.push({
        pathname: ENDPOINT.MANAGER_GIFT_LIST,
        search: getParamsFormObject({ ...urlParams }),
      });
    },
  });

  const fetchGifts = useCallback(() => {
    actionGetGifts({
      payload: buildGiftListPayload({
        page: searchQueries.page,
        limit: DEFAULT_LIMIT,
        q: searchQueries.q,
        giftType: searchQueries.giftType,
        statusType: searchQueries.statusType,
      }),
    });
  }, [actionGetGifts, searchQueries]);

  useEffect(() => {
    fetchGifts();
  }, [fetchGifts]);

  const onDeleteGift = useCallback((giftItem) => {
    const giftId = giftItem.id || giftItem._id;
    if (!giftId) return;
    setGiftIdDelete(giftId);
    onOpenModalDeleteGift();
  }, [onOpenModalDeleteGift]);

  const onConfirmDeleteGift = useCallback(() => {
    actionDeleteGift({
      id: giftIdDelete,
      callback: () => {
        onCloseModalDeleteGift();
        fetchGifts();
      },
    });
  }, [actionDeleteGift, giftIdDelete, onCloseModalDeleteGift, fetchGifts]);

  const onChangeFilterOption = useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);
  }, [validation]);

  const onResetForm = useCallback(() => {
    validation.resetForm();
    history.replace({
      pathname: ENDPOINT.MANAGER_GIFT_LIST,
      search: searchQueries.statusType
        ? getParamsFormObject({ page: 1, statusType: searchQueries.statusType })
        : '',
    });
  }, [history, searchQueries.statusType, validation]);

  const onSelectFilterByStatus = useCallback((status) => () => {
    const urlParams = {
      page: 1,
      q: searchQueries.q,
      giftType: searchQueries.giftType,
    };
    if (status) urlParams.statusType = status;
    history.push({
      pathname: ENDPOINT.MANAGER_GIFT_LIST,
      search: getParamsFormObject(urlParams),
    });
  }, [history, searchQueries]);

  const onChangePage = useCallback((selected) => {
    const nextPage = selected.selected + 1;
    const urlParams = {
      page: nextPage,
      q: searchQueries.q,
      giftType: searchQueries.giftType,
    };
    if (searchQueries.statusType) urlParams.statusType = searchQueries.statusType;
    history.push({
      pathname: ENDPOINT.MANAGER_GIFT_LIST,
      search: getParamsFormObject(urlParams),
    });
  }, [history, searchQueries]);

  const onAddGift = useCallback(() => {
    history.push(ENDPOINT.MANAGER_GIFT_CREATE);
  }, [history]);

  const onViewGiftDetail = useCallback((gift) => {
    const giftId = gift?._id || gift?.id;
    if (giftId) {
      history.push(ENDPOINT.MANAGER_GIFT_DETAIL(giftId));
    }
  }, [history]);

  const isSearchBtnDisabled = useMemo(
    () => validation.values.q.trim() === '' && validation.values.giftType === '',
    [validation.values.q, validation.values.giftType],
  );

  const giftCountLabel = useMemo(
    () => `${giftCountFormatter.format(gifts.total)} quà tặng`,
    [gifts.total],
  );

  const totalPage = Math.ceil(gifts.total / DEFAULT_LIMIT) || 1;
  const activeTab = searchQueries.statusType || GIFT_LIST_TAB.ALL;
  const giftList = useMemo(
    () => (gifts?.data || []).map(normalizeGiftFromApi),
    [gifts?.data],
  );

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name={PAGE_TITLE} />

      <Loading loading={isLoadingGetGifts} loaderOnPage />

      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <Card className="Section">
          <form onSubmit={validation.handleSubmit}>
            <div className={styles.FilterRow}>
              <div className={styles.FilterFields}>
                <div className={styles.FilterSearchCol}>
                  <Label className="Section__Search__Label">
                    Tìm kiếm
                  </Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      name="q"
                      id="q"
                      type="text"
                      placeholder="Tìm kiếm theo Tên quà tặng"
                      validation={validation}
                      inputClassName={cn('Section__Search__Input', styles.FilterSearchInput)}
                    />

                    <i className="fas fa-search Section__Search__Icon__Search" />
                  </div>
                </div>

                <div className={styles.FilterSelectCol}>
                  <Label className="Section__Search__Label">
                    Bộ lọc
                  </Label>

                  <AppSelectValidation
                    placeholder="Theo Loại quà"
                    name="giftType"
                    keyName="giftType"
                    options={GIFT_TYPE_FILTER_OPTIONS}
                    validation={validation}
                    value={validation.values.giftType}
                    onChange={onChangeFilterOption}
                    isHiddenSearchable
                    isHiddenClearable
                    selectedClassName={styles.GiftTypeSelect}
                  />
                </div>
              </div>

              <div className={styles.FilterActionsCol}>
                <Button
                  outline
                  classes="Section__Search__Button__ReType mr-24"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </div>
          </form>
        </Card>

        <Card className="Section">
          <div className="Tab__Header__Container">
            <div className="Tab__Header">
              <nav className="Tab__Header__Nav" aria-label="Lọc trạng thái quà tặng">
                {NAV_TAB.map((tab) => (
                  <div key={tab.value} className="Tab__Header__Tab">
                    <button
                      type="button"
                      onClick={onSelectFilterByStatus(tab.value)}
                      className={`Tab__Header__Tab__Item ${
                        (searchQueries.statusType === tab.value
                          || (tab.value === GIFT_LIST_TAB.ALL && !searchQueries.statusType))
                        && 'Active'
                      }`}
                    >
                      {tab.title}
                    </button>
                  </div>
                ))}
              </nav>
            </div>

            <Button
              type="button"
              classes="Tab__Header__Button"
              onClick={onAddGift}
            >
              <img src={AddWhiteIcon} alt="Add icon" />
              <span className="Tab__Header__Button__Text">Thêm quà tặng</span>
            </Button>
          </div>

          <TabContent activeTab={activeTab} className="Table__Body">
            <Label className="Table__Body__Label">
              {giftCountLabel}
            </Label>

            {NAV_TAB.map((item) => (
              <TabPane tabId={item.value} key={item.value}>
                <DataTableListGift
                  gifts={giftList}
                  onViewGiftDetail={onViewGiftDetail}
                  onDeleteGift={onDeleteGift}
                />
              </TabPane>
            ))}
          </TabContent>

        </Card>
        {totalPage > 1 && (
        <Pagination
          page={searchQueries.page}
          pageCount={totalPage}
          onChangePage={onChangePage}
        />
        )}
      </Container>

      <ModalRequestAction
        isOpen={isOpenModalDeleteGift}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xóa \n quà tặng này không?'}
        onToggle={onToggleModalDeleteGift}
        onCloseModal={onCloseModalDeleteGift}
        onConfirm={onConfirmDeleteGift}
      />
    </div>
  );
}

ManagerGiftList.propTypes = {
  gifts: PropTypes.shape({
    data: PropTypes.instanceOf(Array),
    total: PropTypes.number,
  }),
  isLoadingGetGifts: PropTypes.bool,
  actionGetGifts: PropTypes.func,
  actionDeleteGift: PropTypes.func,
};

ManagerGiftList.defaultProps = {
  gifts: { data: [], total: 0 },
  isLoadingGetGifts: false,
  actionGetGifts: INITIAL_VALUE.FUNCTION,
  actionDeleteGift: INITIAL_VALUE.FUNCTION,
};

export default ManagerGiftList;
